import { Link } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import ScrollReveal from "@/components/base/ScrollReveal";
import TeamPortraitStage from "@/components/base/TeamPortraitStage";
import CurrentGroupOfferings from "@/components/feature/CurrentGroupOfferings";
import { clinicalTeam, type TeamMember } from "@/data/therapists";
import { providerAvailability } from "@/data/providerAvailability.js";

import childHeroImage from "../../media/child theraphy hero image.png";
import childSupportingImage from "../../media/Child theraphy supporting image.png";

import { ACTIVE_BOOKING_URL as BOOKING_URL } from "@/config/bookingLinks.js";

const childTherapyDetails = [
  {
    label: "Age range",
    value: "Children ages 6-12",
  },
  {
    label: "Session type",
    value: "Individual child therapy",
  },
  {
    label: "Available support",
    value: "In-person in Larchmont, NY and telehealth where clinically appropriate",
  },
  {
    label: "Common concerns",
    value:
      "Anxiety, emotional regulation, school stress, self-esteem, behavior concerns, friendship issues, and family transitions",
  },
  {
    label: "Parent involvement",
    value:
      "Parents and caregivers may be included as clinically appropriate to support progress outside of sessions",
  },
];

const issues = [
  "Big feelings or frequent emotional overwhelm",
  "Difficulty expressing emotions in healthy ways",
  "School-related stress or avoidance",
  "Friendship challenges and social stress",
  "Family transitions, grief, or major life changes",
  "Behavior concerns connected to stress or anxiety",
  "Self-esteem and confidence concerns",
  "Worry, fear, or difficulty calming down",
];

const whatToExpect = [
  {
    num: "01",
    title: "A safe, age-appropriate space",
    body:
      "Child therapy gives children a supportive place to express feelings, build trust, and work through challenges in a way that feels approachable and developmentally appropriate.",
    color: "#B4A6E3",
  },
  {
    num: "02",
    title: "Practical emotional tools",
    body:
      "Children learn tools for naming emotions, calming their bodies, communicating needs, managing worry, and responding to difficult moments with more confidence.",
    color: "#7D54FC",
  },
  {
    num: "03",
    title: "Support for parents and caregivers",
    body:
      "When clinically helpful, parents and caregivers may be included so the skills developed in therapy can be supported at home, at school, and in everyday routines.",
    color: "#3C6FF8",
  },
  {
    num: "04",
    title: "Care that fits the child",
    body:
      "Each child is different. Therapy is tailored to the child’s personality, communication style, needs, and strengths rather than using a one-size-fits-all approach.",
    color: "#6B742F",
  },
];

// Eligibility and ordering come directly from the canonical service data.
const childTherapyProviders = providerAvailability
  .filter(
    (provider) =>
      provider.acceptingChildClients === true &&
      provider.worksWith.includes("Children")
  )
  .sort((a, b) => a.displayOrder - b.displayOrder)
  .map((availability) =>
    clinicalTeam.find((provider) => provider.id === availability.id)
  )
  .filter((provider): provider is TeamMember => Boolean(provider));

export default function ChildTherapyPage() {
  return (
    <>

      <PageLayout>
        {/* Hero */}
        <section className="relative flex min-h-[520px] w-full items-center overflow-hidden md:min-h-[620px]">
          <div className="absolute inset-0 z-0">
            <img
              src={childHeroImage}
              alt="Child therapy for children ages 6-12 at Perspective Therapy"
              className="h-full w-full object-cover object-center"
            />

            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(120deg, rgba(28,20,12,0.48) 0%, rgba(42,30,18,0.32) 50%, rgba(60,40,20,0.14) 100%)",
              }}
            />

            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(to top, rgba(20,14,8,0.38) 0%, transparent 50%)",
              }}
            />
          </div>

          <div className="relative z-10 w-full px-6 pb-20 pt-36 md:px-12 lg:px-20">
            <div className="max-w-2xl">
              <p className="mb-4 font-manrope text-[13px] font-semibold uppercase tracking-widest text-white/90">
                Child Therapy
              </p>

              <h1 className="mb-6 font-fraunces text-4xl font-light leading-[1.15] text-white md:text-5xl lg:text-[3.4rem]">
                Therapy for children
                <br />
                <em
                  className="not-italic font-normal whitespace-nowrap"
                  style={{ color: "#C9BCEE" }}
                >
                  ages 6-12
                </em>
              </h1>

              <div className="max-w-lg rounded-2xl bg-white/80 p-3 backdrop-blur-sm md:p-4">
                <p className="font-manrope text-lg leading-relaxed text-[#2B2B2B]/90">
                  Support for children ages 6-12 navigating anxiety, emotional
                  regulation, school stress, self-esteem, behavior concerns,
                  friendships, and family transitions.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Details */}
        <section className="w-full py-14 md:py-16">
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
              {childTherapyDetails.map((item) => (
                <ScrollReveal key={item.label} variant="fadeUp" delay={0}>
                  <div
                    className="h-full rounded-2xl border bg-white/80 p-6"
                    style={{ borderColor: "rgba(203,191,175,0.35)" }}
                  >
                    <p className="font-manrope text-[12px] font-semibold uppercase tracking-[0.18em] text-[#8B7355]">
                      {item.label}
                    </p>

                    <p className="mt-3 font-manrope text-[14px] leading-relaxed text-[#2B2B2B]/75">
                      {item.value}
                    </p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* What we help with */}
        <section className="w-full py-20 md:py-28">
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-20">
              <ScrollReveal variant="fadeLeft" delay={0}>
                <div className="h-[280px] w-full overflow-hidden rounded-2xl sm:h-[340px] md:h-[500px]">
                  <img
                    src={childSupportingImage}
                    alt="Child therapy support for children ages 6-12"
                    className="h-full w-full object-cover object-center"
                  />
                </div>
              </ScrollReveal>

              <div>
                <ScrollReveal variant="fadeIn" delay={0}>
                  <div className="mb-4 flex items-center gap-2">
                    <div
                      className="h-[2px] w-5 rounded-full"
                      style={{
                        background: "linear-gradient(90deg, #B4A6E3, #7D54FC)",
                      }}
                    />
                    <span
                      className="font-manrope text-[13px] font-semibold uppercase tracking-widest"
                      style={{ color: "#7D54FC" }}
                    >
                      What We Help With
                    </span>
                  </div>
                </ScrollReveal>

                <ScrollReveal variant="fadeUp" delay={80}>
                  <h2 className="mb-6 font-fraunces text-3xl font-light leading-snug text-[#2B2B2B] md:text-4xl">
                    Children show stress in different ways{" "}
                    <em className="not-italic" style={{ color: "#7D54FC" }}>
                      therapy helps make sense of it
                    </em>
                  </h2>
                </ScrollReveal>

                <ScrollReveal variant="fadeUp" delay={160}>
                  <p className="mb-8 font-manrope text-[15px] leading-relaxed text-[#2B2B2B]/82">
                    When children are struggling, it may show up through worry,
                    irritability, withdrawal, tearfulness, school avoidance,
                    difficulty focusing, or behavior that feels hard to
                    understand. Child therapy helps children build language,
                    emotional awareness, and practical coping tools while giving
                    caregivers a clearer framework for support.
                  </p>
                </ScrollReveal>

                <ScrollReveal variant="fadeUp" delay={240}>
                  <div className="mb-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {issues.map((issue) => (
                      <div key={issue} className="flex items-center gap-2.5">
                        <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                          <i
                            className="ri-check-line text-sm"
                            style={{ color: "#7D54FC" }}
                          />
                        </span>
                        <span className="font-manrope text-[14px] text-[#2B2B2B]/80">
                          {issue}
                        </span>
                      </div>
                    ))}
                  </div>
                </ScrollReveal>

                <ScrollReveal variant="fadeUp" delay={320}>
                  <div className="flex flex-col gap-4 sm:flex-row">
                    <a
                      href={BOOKING_URL}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full px-7 py-4 font-manrope text-[15px] font-semibold text-white transition-colors"
                      style={{ background: "#7D54FC" }}
                      onMouseEnter={(e) =>
                        ((e.currentTarget as HTMLAnchorElement).style.background =
                          "#6a40e8")
                      }
                      onMouseLeave={(e) =>
                        ((e.currentTarget as HTMLAnchorElement).style.background =
                          "#7D54FC")
                      }
                    >
                      Book Now <i className="ri-arrow-right-line" />
                    </a>

                    <a
                      href="#providers"
                      className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap font-manrope text-[15px] font-medium text-[#2B2B2B]/78 transition-colors hover:text-[#2B2B2B]"
                    >
                      View child therapy providers{" "}
                      <i className="ri-arrow-right-line" />
                    </a>
                  </div>
                </ScrollReveal>
              </div>
            </div>
          </div>
        </section>

        {/* What to expect */}
        <section
          className="w-full py-20 md:py-28"
          style={{
            background:
              "linear-gradient(180deg, #F8F5EE 0%, #F5F1E8 45%, #F2EDE4 100%)",
          }}
        >
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="mb-14 max-w-3xl">
              <ScrollReveal variant="fadeIn" delay={0}>
                <span
                  className="mb-4 block font-manrope text-[13px] font-semibold uppercase tracking-widest"
                  style={{ color: "#6B742F" }}
                >
                  What to Expect
                </span>
              </ScrollReveal>

              <ScrollReveal variant="fadeUp" delay={80}>
                <h2 className="mb-5 font-fraunces text-3xl font-light leading-snug text-[#2B2B2B] md:text-4xl">
                  Child therapy that is supportive, practical, and family-aware
                </h2>
              </ScrollReveal>

              <ScrollReveal variant="fadeUp" delay={160}>
                <p className="font-manrope text-[15px] leading-relaxed text-[#2B2B2B]/78">
                  Our child therapy approach is collaborative and paced around
                  the child’s needs. The goal is to help children feel safe,
                  understood, and supported while building skills they can use
                  outside of therapy.
                </p>
              </ScrollReveal>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:gap-8">
              {whatToExpect.map((item, i) => (
                <ScrollReveal key={item.num} variant="fadeUp" delay={i * 90}>
                  <div
                    className="rounded-2xl border bg-white/80 p-7"
                    style={{ borderColor: item.color + "22" }}
                  >
                    <div
                      className="mb-5 h-[3px] w-10 rounded-full"
                      style={{ background: item.color }}
                    />

                    <div className="flex items-start gap-4">
                      <span
                        className="mt-0.5 shrink-0 font-manrope text-sm font-bold"
                        style={{ color: item.color }}
                      >
                        {item.num}
                      </span>

                      <div>
                        <h3 className="mb-2 font-fraunces text-lg font-medium text-[#2B2B2B]">
                          {item.title}
                        </h3>

                        <p className="font-manrope text-[15px] leading-relaxed text-[#2B2B2B]/78">
                          {item.body}
                        </p>
                      </div>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        <CurrentGroupOfferings />

        {/* Providers */}
        <section id="providers" className="w-full py-20 md:py-28">
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="mb-12 max-w-3xl">
              <ScrollReveal variant="fadeIn" delay={0}>
                <span
                  className="mb-4 block font-manrope text-[13px] font-semibold uppercase tracking-widest"
                  style={{ color: "#7D54FC" }}
                >
                  Available Providers
                </span>
              </ScrollReveal>

              <ScrollReveal variant="fadeUp" delay={80}>
                <h2 className="mb-5 font-fraunces text-3xl font-light leading-snug text-[#2B2B2B] md:text-4xl">
                  Child therapy providers at Perspective Therapy
                </h2>
              </ScrollReveal>

              <ScrollReveal variant="fadeUp" delay={160}>
                <p className="font-manrope text-[15px] leading-relaxed text-[#2B2B2B]/78">
                  Provider availability may vary based on clinical fit, schedule,
                  location, and service needs.
                </p>
              </ScrollReveal>
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
              {childTherapyProviders.map((provider, index) => (
                <ScrollReveal
                  key={provider.name}
                  variant="fadeUp"
                  delay={index * 80}
                >
                  <Link
                    to={"/therapists/" + provider.id}
                    className="group block h-full overflow-hidden rounded-2xl border bg-white/80 transition-transform hover:-translate-y-1"
                    style={{ borderColor: "rgba(203,191,175,0.35)" }}
                  >
                    <TeamPortraitStage src={provider.img} name={provider.name} variant="child" />

                    <div className="p-6">
                      <h3 className="font-fraunces text-xl font-medium text-[#2B2B2B]">
                        {provider.name}
                      </h3>

                      <p className="mt-2 font-manrope text-sm font-semibold text-[#8B7355]">
                        {provider.role}
                      </p>

                      <p className="mt-4 font-manrope text-sm leading-7 text-[#2B2B2B]/70">
                        {provider.shortBio}
                      </p>

                      <span className="mt-5 inline-flex items-center gap-1.5 font-manrope text-sm font-semibold text-[#2B2B2B]">
                        View profile <i className="ri-arrow-right-line" />
                      </span>
                    </div>
                  </Link>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="w-full py-16 md:py-20">
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="mx-auto max-w-3xl text-center">
              <ScrollReveal variant="fadeUp" delay={0}>
                <h2 className="mb-5 font-fraunces text-3xl font-light leading-snug text-[#2B2B2B] md:text-4xl">
                  Ready to find the right support for your child?
                </h2>

                <p className="mx-auto mb-6 max-w-lg font-manrope text-[15px] leading-relaxed text-[#2B2B2B]/78">
                  Whether your child ages 6-12 is struggling with anxiety,
                  emotional regulation, school stress, self-esteem, or changes
                  at home, our team can help you identify the next step.
                </p>
              </ScrollReveal>

              <ScrollReveal variant="fadeUp" delay={80}>
                <div className="flex flex-col justify-center gap-4 sm:flex-row">
                  <a
                    href={BOOKING_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full px-7 py-4 font-manrope text-[15px] font-semibold text-white transition-colors"
                    style={{ background: "#3C6FF8" }}
                    onMouseEnter={(e) =>
                      ((e.currentTarget as HTMLAnchorElement).style.background =
                        "#2a5de6")
                    }
                    onMouseLeave={(e) =>
                      ((e.currentTarget as HTMLAnchorElement).style.background =
                        "#3C6FF8")
                    }
                  >
                    Book Now <i className="ri-arrow-right-line" />
                  </a>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>
      </PageLayout>
    </>
  );
}
